import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth'
import { api, ApiError } from '../api'
import { useI18n } from '../i18n'
import type { Booking } from '../types'
import { addDays, formatHour, formatLong, parseYmd, todayYmd, ymd } from '../lib/dates'
import { Empty, RoleChip, Spinner, useConfirm, useToast } from '../components/ui'

export default function MyShifts() {
  const toast = useToast()
  const confirm = useConfirm()
  const { t } = useI18n()
  const { user } = useAuth()
  const [bookings, setBookings] = useState<Booking[]>([])
  const [available, setAvailable] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [showPast, setShowPast] = useState(false)
  const [actingId, setActingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    const today = parseYmd(todayYmd())
    try {
      const [mine, offered] = await Promise.all([
        api.bookings(ymd(addDays(today, -45)), ymd(addDays(today, 180)), 'mine'),
        api.offeredBookings(ymd(today), ymd(addDays(today, 180))),
      ])
      setBookings(mine.bookings.filter((b) => b.status !== 'cancelled' && b.status !== 'rejected'))
      // Don't list your own offered shifts as "available to take".
      setAvailable(offered.bookings.filter((b) => b.user_id !== user?.id))
    } catch {
      toast.push(t('my.loadErr'), 'error')
    } finally {
      setLoading(false)
    }
  }, [toast, user?.id])

  useEffect(() => {
    load()
  }, [load])

  async function offer(b: Booking, on: boolean) {
    setActingId(b.id)
    try {
      if (on) await api.offerBooking(b.id)
      else await api.unofferBooking(b.id)
      toast.push(on ? t('toast.offered') : t('toast.withdrawn'), 'ok')
      await load()
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('toast.offerErr'), 'error')
    } finally {
      setActingId(null)
    }
  }

  async function take(b: Booking) {
    setActingId(b.id)
    try {
      await api.takeBooking(b.id)
      toast.push(t('toast.taken'), 'ok')
      await load()
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('toast.takeErr'), 'error')
    } finally {
      setActingId(null)
    }
  }

  const { upcoming, past } = useMemo(() => {
    const t = todayYmd()
    const up: Record<string, Booking[]> = {}
    const pa: Record<string, Booking[]> = {}
    for (const b of bookings) {
      const target = b.date >= t ? up : pa
      ;(target[b.date] ||= []).push(b)
    }
    return { upcoming: up, past: pa }
  }, [bookings])

  async function cancel(b: Booking) {
    if (!(await confirm())) return
    setActingId(b.id)
    try {
      if (b.status === 'pending') await api.deleteBooking(b.id)
      else await api.updateBooking(b.id, 'cancelled')
      toast.push(t('toast.cancelled'), 'ok')
      await load()
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('toast.cancelErr'), 'error')
    } finally {
      setActingId(null)
    }
  }

  if (loading) {
    return (
      <div className="full-loader" style={{ minHeight: 320 }}>
        <Spinner large />
      </div>
    )
  }

  const upcomingDates = Object.keys(upcoming).sort()
  const pastDates = Object.keys(past).sort().reverse()

  return (
    <div>
      <div className="page-head">
        <h1>{t('my.title')}</h1>
        <p>{t('my.subtitle')}</p>
      </div>

      {upcomingDates.length === 0 ? (
        <div className="card">
          <Empty title={t('my.none.title')}>
            <p className="muted">{t('my.none.hint')}</p>
            <Link to="/calendar" className="btn btn-primary mt-16">
              {t('topbar.bookShift')}
            </Link>
          </Empty>
        </div>
      ) : (
        <div className="col gap-16">
          {upcomingDates.map((date) => (
            <DayGroup
              key={date}
              date={date}
              items={upcoming[date]}
              actingId={actingId}
              onCancel={cancel}
              onOffer={offer}
            />
          ))}
        </div>
      )}

      <div className="mt-24">
        <div className="page-head">
          <h2>{t('my.available')}</h2>
          <p>{t('my.availableHint')}</p>
        </div>
        {available.length === 0 ? (
          <div className="card">
            <Empty title={t('my.noAvailable')} />
          </div>
        ) : (
          <div className="list card">
            {available.map((b) => (
              <div className="list-row" key={b.id}>
                <div className="grow">
                  <div className="title">
                    {formatLong(b.date)} · {formatHour(b.hour)}–{formatHour(b.hour + 1)}
                  </div>
                  <div className="sub">{b.user_name || b.user_email}</div>
                </div>
                {b.role_name && <RoleChip name={b.role_name} color={b.role_color} />}
                <span className="badge purple">{t('my.offered')}</span>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => take(b)}
                  disabled={actingId === b.id}
                >
                  {actingId === b.id ? <Spinner /> : t('my.take')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {pastDates.length > 0 && (
        <div className="mt-24">
          <button className="btn btn-ghost btn-sm" onClick={() => setShowPast((v) => !v)}>
            {showPast ? t('my.hidePast') : t('my.showPast', { n: pastDates.length })}
          </button>
          {showPast && (
            <div className="col gap-16 mt-16">
              {pastDates.map((date) => (
                <DayGroup key={date} date={date} items={past[date]} past />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function DayGroup({
  date,
  items,
  actingId,
  onCancel,
  onOffer,
  past,
}: {
  date: string
  items: Booking[]
  actingId?: string | null
  onCancel?: (b: Booking) => void
  onOffer?: (b: Booking, on: boolean) => void
  past?: boolean
}) {
  const { t } = useI18n()
  const sorted = [...items].sort((a, b) => a.hour - b.hour)
  return (
    <div className="card">
      <div className="card-head">
        <h3>{formatLong(date)}</h3>
        <span className="tag">{t('my.hours', { n: items.length })}</span>
      </div>
      <div className="list">
        {sorted.map((b) => (
          <div className="list-row" key={b.id}>
            <div className="grow">
              <div className="title">{formatHour(b.hour)}–{formatHour(b.hour + 1)}</div>
              {b.note && <div className="sub">{b.note}</div>}
            </div>
            {b.role_name && <RoleChip name={b.role_name} color={b.role_color} />}
            <span className={`badge ${b.status === 'approved' ? 'green' : 'amber'}`}>
              {b.status === 'approved' ? t('status.approved') : t('status.pending')}
            </span>
            {b.offered_at && <span className="badge purple">{t('my.offered')}</span>}
            {!past && onOffer && b.status === 'approved' && (
              <button className="btn btn-ghost btn-sm" onClick={() => onOffer(b, !b.offered_at)} disabled={actingId === b.id}>
                {actingId === b.id ? <Spinner /> : b.offered_at ? t('my.withdraw') : t('my.offer')}
              </button>
            )}
            {!past && onCancel && (
              <button className="btn btn-ghost btn-sm" onClick={() => onCancel(b)} disabled={actingId === b.id}>
                {actingId === b.id ? <Spinner /> : t('common.cancel')}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
